import { useCallback, useRef, useState, type ReactNode } from "react";

import { Modal } from "../Modal";
import { ConfirmContext, type ConfirmOptions } from "./context";

interface Request extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

let counter = 0;

/** One confirm dialog at a time; further requests queue behind it. */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [queue, setQueue] = useState<Request[]>([]);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const cancelRef = useRef<HTMLButtonElement>(null);

  const confirm = useCallback(
    (options: ConfirmOptions) =>
      new Promise<boolean>((resolve) => {
        setQueue((q) => [...q, { ...options, id: ++counter, resolve }]);
      }),
    [],
  );

  const current = queue[0];
  const settle = (ok: boolean) => {
    if (!current) return;
    current.resolve(ok);
    setQueue((q) => q.filter((r) => r.id !== current.id));
  };
  const danger = current?.tone === "danger";

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {current ? (
        <Modal
          key={current.id}
          open
          onClose={() => settle(false)}
          title={current.title}
          widthClass="modal--confirm"
          initialFocusRef={danger ? cancelRef : confirmRef}
          footer={
            <>
              <button
                ref={cancelRef}
                type="button"
                className="btn btn-ghost"
                onClick={() => settle(false)}
              >
                {current.cancelLabel ?? "Cancel"}
              </button>
              <button
                ref={confirmRef}
                type="button"
                className={danger ? "btn btn-second btn-danger" : "btn btn-main"}
                onClick={() => settle(true)}
              >
                {current.confirmLabel ?? "Confirm"}
              </button>
            </>
          }
        >
          {current.body ? (
            <div className="text-sm text-ink-medium leading-relaxed">{current.body}</div>
          ) : null}
        </Modal>
      ) : null}
    </ConfirmContext.Provider>
  );
}
